import { useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'

import {
  EmailTemplateDetails,
  EmailTemplateResponse,
  EmailTemplateStatus,
  SendTestEmailRequest,
  getEmailTemplateDetails,
  getEmailTemplates,
  sendTestEmail,
} from 'core'

import { Button, Col, ConfirmButton, Menu, Notification, Row, Skeleton, Tag } from 'common/components'
import { useCommandRequestV2, useQueryRequestV2 } from 'common/hooks'

import { ROUTE_PATHS } from '../../common/routePaths'
import EmailTemplateContent from './EmailTemplateContent/index'

import './styles.less'

const populateContent = (wrapper: string, content: string, startIndex: number, populationOffset = 0) =>
  wrapper.slice(0, startIndex) + content + wrapper.slice(startIndex + populationOffset)

const tinymceScript = `
<script src="https://cdn.tiny.cloud/1/isjqpkfryeci65mningt7lnuzbe7f8xsxi2wrwptpmnl327a/tinymce/6/tinymce.min.js" referrerpolicy="origin"></script>
<script>
  tinymce.init({
    selector: '#edit-content',
    menubar: false,
    inline: true,
    plugins: ['link', 'lists', 'autolink'],
    toolbar: [
      'undo redo | bold italic underline | fontselect fontsizeselect',
      'forecolor backcolor | alignleft aligncenter alignright alignfull | numlist bullist outdent indent',
    ],
    valid_elements: 'p[style],strong,em,span[style],a[href],ul,ol,li,tr,td,b,br,center',
    valid_styles: {
      '*': 'font-size,font-family,color,text-decoration,text-align',
    },
  })
</script>
`

const tinymceHTML = `
<tr>
  <td>
  <table>
    <tbody>
      <div id="edit-content">
      </div>
    </tbody>
  </table>
  </td>
</tr>
`

const ViewEmailTemplates = () => {
  const history = useHistory()

  const {
    data: selectedTemplate,
    isLoading: selectedTemplateIsLoading,
    sendQuery: sendTemplateDetailsQuery,
  } = useQueryRequestV2<EmailTemplateDetails>({ initialState: null })

  const { data: emailTemplates, isLoading: templatesAreLoading } = useQueryRequestV2<EmailTemplateResponse[]>({
    initialState: [],
    queryFn: getEmailTemplates,
  })

  const { sendCommand: sendTestEmailCommand, isLoading: isSendTestEmailLoading } = useCommandRequestV2(
    (arg: SendTestEmailRequest) => sendTestEmail(arg),
  )

  const [isEditing, setIsEditing] = useState(false)

  const contentToWrite = (() => {
    if (!selectedTemplate) return

    const { templateContent, wrapperContent } = selectedTemplate

    const hydrationLookup = '</title>'
    const hydrationAreaStartIndex = wrapperContent.indexOf(hydrationLookup) + hydrationLookup.length

    const hydratedWrapper = !isEditing
      ? wrapperContent
      : populateContent(wrapperContent, tinymceScript, hydrationAreaStartIndex)

    return !isEditing
      ? hydratedWrapper.replace('||*CONTENT*||', templateContent)
      : hydratedWrapper.replace('||*CONTENT*||', tinymceHTML) // * Template content is removed to comply with tinymce
  })()

  const templatesList = useMemo(
    () => (
      <Menu
        className="no-right-border templates-menu"
        items={emailTemplates.map(({ id, subject, status, product }, key) => ({
          key,
          label: (
            <>
              {subject} <br />
              ID: <b>{id}</b> <br />
              {product && (
                <>
                  Product: <b>{product.name}</b> <br />
                </>
              )}
              {status === EmailTemplateStatus.Draft ? (
                <Tag color={'orange'}>Draft</Tag>
              ) : (
                <Tag color={'green'}>Published</Tag>
              )}
            </>
          ),
          className: 'title-content-wrapper pt-8 template-menu-item',
          onClick: () => {
            setIsEditing(false)
            sendTemplateDetailsQuery(() => getEmailTemplateDetails(id))
          },
        }))}
      />
    ),
    [emailTemplates],
  )

  return (
    <div className="super-wrapper">
      <Row className="inner-padding">
        <Col span={4} className="text-semi-bold">
          Email Templates
        </Col>
      </Row>

      <Skeleton loading={templatesAreLoading}>
        <Row className="inner-padding">
          <Col span={8} className="header-wrapper">
            <Row className="pb-8 bottom-border" justify={'space-between'}>
              <Col>Templates</Col>
              <Col>
                <Button
                  onClick={() => {
                    history.push(ROUTE_PATHS.ADD_EMAIL_TEMPLATES)
                  }}
                  type="primary"
                >
                  New template
                </Button>
              </Col>
            </Row>

            <Row>
              <Col span={24} className="pt-8">
                {templatesList}
              </Col>
            </Row>
          </Col>
          <Col span={16} className="header-wrapper pl-24">
            {selectedTemplate && (
              <Row className="pb-8 bottom-border" justify={'space-between'}>
                <Col span={18}>Subject: {selectedTemplate.subject}</Col>

                <Col>
                  {selectedTemplate.status === EmailTemplateStatus.Published && (
                    <>
                      <Button
                        icon="fa fa-pencil-alt"
                        type="primary"
                        className={'mr-8'}
                        onClick={() =>
                          history.push(`${ROUTE_PATHS.EDIT_EMAIL_TEMPLATES}/${selectedTemplate.templateId}`)
                        }
                      >
                        Edit
                      </Button>

                      <ConfirmButton
                        icon="fa fa-paper-plane"
                        confirmModalTitle={'Send Test Email'}
                        confirmModalContent={'Are you sure you want to send a test email?'}
                        confirmOkButtonText={'Send'}
                        loading={isSendTestEmailLoading}
                        disabled={isSendTestEmailLoading}
                        onConfirm={async () => {
                          const result = await sendTestEmailCommand({
                            templateId: selectedTemplate.templateId,
                          })

                          if (result.completed) {
                            Notification.success('Test email sent successfully')
                          }
                        }}
                      >
                        Send Test Email
                      </ConfirmButton>
                    </>
                  )}
                </Col>
              </Row>
            )}

            <Row>
              <Col className="title-content-wrapper pt-20 w-100">
                <EmailTemplateContent
                  loading={selectedTemplateIsLoading}
                  selectedTemplate={selectedTemplate}
                  contentToWrite={contentToWrite}
                  isEditing={isEditing}
                  onEdit={(templateId, usingDefaultEditor = true) => {
                    templateId && sendTemplateDetailsQuery(() => getEmailTemplateDetails(templateId))
                    usingDefaultEditor && setIsEditing((prev) => !prev)
                  }}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Skeleton>
    </div>
  )
}

export default ViewEmailTemplates
